import { useId, type ChangeEvent, type FormEvent } from 'react';

import styles from './EmptyState.module.css';

interface Props {
  /** Mobile shows the URL row and upload control here; desktop has them in the top bar. */
  showInputs: boolean;
  url: string;
  loading: boolean;
  error: string | null;
  onUrlChange: (url: string) => void;
  onLoad: () => void;
  onFile: (file: File) => void;
}

/** Copy from the handoff. Desktop uses the longer body at a larger scale. */
const BODY_MOBILE =
  "Load a busy picture — a group shot, a messy kitchen, a holiday street — and we'll " +
  'black it out into 81 guessable squares.';
const BODY_DESKTOP =
  'Paste a link up top or upload a file — a group shot, a messy kitchen, a holiday street. ' +
  "We'll black it out into 81 guessable squares and hand you a matching answer key.";

/** The step-1 block, shown inside the picture stage before anything is loaded. */
export function EmptyState({
  showInputs,
  url,
  loading,
  error,
  onUrlChange,
  onLoad,
  onFile,
}: Props): React.JSX.Element {
  const fileId = useId();

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    onLoad();
  };

  const pick = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) onFile(file);
    // Reset so picking the same file twice still fires a change event.
    event.target.value = '';
  };

  return (
    <div className={styles.empty}>
      <p className={styles.kicker}>Round 1 · pick your picture</p>
      <h1 className={styles.headline}>Where&apos;s the cat?</h1>
      <p className={styles.body}>{showInputs ? BODY_MOBILE : BODY_DESKTOP}</p>

      {showInputs && (
        <>
          <form className={styles.row} onSubmit={submit}>
            <input
              className={`input ${styles.urlInput}`}
              type="url"
              inputMode="url"
              placeholder="Paste a picture URL"
              value={url}
              onChange={(event) => onUrlChange(event.target.value)}
              aria-label="Picture URL"
            />
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Loading…' : 'Load'}
            </button>
          </form>

          <label className={`btn btn-secondary ${styles.upload}`} htmlFor={fileId}>
            Upload from this phone
            <input
              id={fileId}
              className={styles.fileInput}
              type="file"
              accept="image/*"
              onChange={pick}
            />
          </label>
        </>
      )}

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
